import React from "react";

export function Spinner({ className = "" }) {
  return (
    <svg className={`animate-spin text-accent ${className}`} width="20" height="20" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-20" />
      <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

export function Loading({ label = "Loading" }) {
  return (
    <div className="flex items-center justify-center gap-3 py-24 text-slate-400" data-testid="loading">
      <Spinner /> <span className="text-sm">{label}…</span>
    </div>
  );
}

export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-8 animate-fade-up">
      <div>
        <h1 className="font-head text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{subtitle}</p>}
      </div>
      <div className="flex items-center gap-2">{children}</div>
    </div>
  );
}

export function Card({ className = "", interactive = false, children, ...rest }) {
  return <div className={`card ${interactive ? "card-i" : ""} ${className}`} {...rest}>{children}</div>;
}

const STATUS = {
  ACTIVE: "ok", APPROVED: "ok", PRESENT: "ok", DONE: "ok", PAID: "ok", REIMBURSED: "ok", READY_NOW: "ok", COMPLETED: "ok", RESOLVED: "ok", HIRED: "ok",
  PENDING: "warn", DRAFT: "warn", IN_PROGRESS: "warn", INITIATED: "warn", SCREENING: "warn", OPEN: "warn", PLANNED: "warn", APPLIED: "info", TODO: "info", INTERVIEW: "info",
  REJECTED: "bad", TERMINATED: "bad", INACTIVE: "bad", ABSENT: "bad", OVERDUE: "bad",
  HIGH: "bad", URGENT: "bad", MEDIUM: "warn", LOW: "info", NORMAL: "info",
};
export function Badge({ value, children, tone }) {
  const t = tone || STATUS[value] || "neutral";
  const map = {
    ok: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400",
    warn: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400",
    bad: "bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400",
    info: "bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-400",
    neutral: "bg-slate-100 text-slate-600 dark:bg-slate-700/40 dark:text-slate-300",
  };
  return <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${map[t]}`}>{children || value}</span>;
}

export function Avatar({ name = "", url, size = 36 }) {
  const initials = name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
  if (url) return <img src={url} alt={name} className="rounded-full object-cover" style={{ width: size, height: size }} />;
  return (
    <div className="flex items-center justify-center rounded-full bg-accent/10 text-accent font-semibold"
      style={{ width: size, height: size, fontSize: size * 0.4 }}>{initials || "?"}</div>
  );
}

export function EmptyState({ icon: Icon, title, hint }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="empty-state">
      {Icon && <Icon className="mb-3 text-slate-300 dark:text-slate-600" size={40} />}
      <p className="font-medium text-slate-600 dark:text-slate-300">{title}</p>
      {hint && <p className="mt-1 text-sm text-slate-400">{hint}</p>}
    </div>
  );
}

export function Modal({ open, onClose, title, children, footer }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="modal">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg card p-6 animate-fade-up max-h-[90vh] overflow-y-auto">
        <h3 className="font-head text-lg font-bold text-slate-900 dark:text-white mb-4">{title}</h3>
        {children}
        {footer && <div className="mt-6 flex justify-end gap-2">{footer}</div>}
      </div>
    </div>
  );
}

export function AccessDenied({ title = "Access restricted", hint = "You don't have permission to view this page." }) {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="access-denied">
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-red-500/10 text-red-500">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
      </div>
      <h2 className="font-head text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white">{title}</h2>
      <p className="mt-2 max-w-sm text-sm text-slate-500 dark:text-slate-400">{hint}</p>
    </div>
  );
}

export const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");
export const fmtMoney = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
